import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Alert, Box, Button, Checkbox, Chip, FormControlLabel, IconButton, InputAdornment,
    MenuItem, Paper, Popover, Select, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
    TextField, Tooltip, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";

import FormModal from "../../../../component/common/FormModal";
import ResizableTable from "../../../../component/common/ResizableTable";
import TablePaginator, { TableSizeSelect } from "../../../../component/common/TablePaginator";
import ActionButton from "../../../../component/common/ActionButton";
import FormField from "../../../../component/common/FormField";
import LoadingDots from "../../../../component/common/LoadingDots";
import VendorAutocomplete from "../../components/vendor/VendorAutocomplete";
import FinanceEntryModal from "../../components/FinanceEntryModal";
import { financeApi, fmtKRWFull, dailyAccountBalances } from "../../api/financeApi";
import { ds } from "../../../../styles/designSystem";
import { formatDate, today as getToday } from "../../../../util/dateUtils";
import { formatNumberWithCommas, toNumericInputState } from "../../../../util/numberFormat";
import { runApiAction } from "../../../../util/apiFeedback";
import { notify } from "../../../../util/toast";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { confirm as confirmDialog } from "../../../../util/confirmDialog";
import { useMyPermissions } from "../../../../util/useMyPermissions";
import { useTableSort } from "../../../../util/useTableSort";
import SearchField, { searchMatch } from "../../../../component/common/SearchField";
import ComboBox from "../../../../component/common/ComboBox";
import { statusOptions, statusInfo, INCOME_STATUS_VALUES } from "../../constants/cashPlanStatus";
import { CATEGORY_SUGGESTIONS, PAYMENT_TYPES } from "../../constants/cashPlan";

// 상태 메타/옵션/수입 허용상태는 constants/cashPlanStatus.js 단일 원천 사용(중복 정의 제거).
const STATUS_OPTIONS = statusOptions();
// 행 유형별 선택 가능한 상태:
//  · 라이브 결재 예정 → 예정/우선지급/지급확정/홀딩 (완료는 '결재 연동 내역' 탭에서 지출확정)
//  · 라이브 미수금 예정 → 예정/홀딩/완료 (완료 = 수금완료)
//  · 저장 지출 행 → 전체 / 저장 입금 행 → 예정·완료
const statusOptionsFor = (row, current) => {
    let opts;
    if (row.virtual) {
        // 결재/미수금 파생 예정 행은 원본 시스템이 상태를 관리 → 현금흐름표에선 상태 변경 불가(현재 상태만 표기).
        opts = STATUS_OPTIONS.filter(o => o.value === current);
    } else if (row.flowType !== "INCOME") {
        opts = STATUS_OPTIONS;
    } else {
        opts = STATUS_OPTIONS.filter(o => INCOME_STATUS_VALUES.includes(o.value));
    }
    if (current && !opts.some(o => o.value === current)) {
        const extra = STATUS_OPTIONS.find(o => o.value === current);
        if (extra) opts = [...opts, extra];
    }
    return opts;
};

const EMPTY_PLAN = {
    flowType: "EXPENSE", title: "", amount: "", scheduledDate: formatDate(getToday()),
    deptId: "", channelId: "", channelName: "", category: "",
    paymentType: "일반", vendorId: null, vendorName: "", note: "",
};

// 현금 흐름표는 연속 예측이라 특정 월/기간이 아니라 넓은 고정 구간을 한 번에 불러온다.
// 완료 건은 [오늘-1년] 이후만 표시용으로, 미완료(예정)는 예정일이 오늘 이전이어도 항상 포함된다.
const LOAD_BACK_DAYS = 365;      // 완료 이력 표시 범위(과거)
const LOAD_FWD_DAYS  = 1825;     // 예측 범위(미래, 약 5년) — 모든 예정 건 포괄

// startDate/endDate 는 '고정비 달력' 탭과 같은 엔티티를 편집하므로 반드시 포함해야 한다.
//   [기존 버그] 이 폼에 기간 필드가 없어서, 여기서 템플릿을 수정하면 payload 에 startDate/endDate 가
//   빠진 채 PUT 되고 서버 applyRecurring 이 무조건 덮어써 달력에서 설정한 유효기간이 조용히 삭제됐다.
const EMPTY_TEMPLATE = {
    flowType: "EXPENSE", title: "", amount: "", payDay: 25,
    startDate: "", endDate: "",
    vendorId: null, vendorName: "", category: "고정비/관리비", note: "", active: true,
};

// 컬럼 정렬 비교값 — 대부분 필드명 그대로면 되지만, 입금/출금액은 부호·유형이 달라 별도 추출.
const SORT_ACCESSORS = {
    income:  r => r.flowType === "INCOME"  ? (r.amount || 0) : null,   // 입금액 (지출행은 null → 뒤로)
    expense: r => r.flowType === "EXPENSE" ? (r.amount || 0) : null,   // 출금액 (수입행은 null → 뒤로)
};

/**
 * 현금흐름 클라이언트 재연산 — 기준잔액(현재 보유 현금)에서 예정일 순으로 누적한다.
 * 잔액("그 당시의 잔액")은 기준잔액에서 예정일 순으로 누적하며 엑셀처럼 "모든 행"에 표시한다:
 *   · 미완료(예정) → 반영. 홀딩(HOLD)은 '홀딩 포함 계산'일 때만.
 *   · 완료(COMPLETED) → 기준일자 '이후' 완료분만 반영(기준잔액에 아직 안 잡힌 실현분).
 *     기준일자 '이전' 완료는 이미 기준잔액에 포함돼 있어 잔액을 움직이지 않는다(이중계산 방지).
 *   · 움직이지 않는 행(홀딩·기준일 이전 완료)도 직전 잔액을 그대로 표시해 열이 끊기지 않게 한다.
 *   · '예정 입금/출금' 합계는 미완료(pending)만.
 * 표시(부서·결재일·송금예정일·금액·완료 표시) 필터는 재조회 없이 그 자리에서 다시 계산된다.
 *
 * <b>서버 요약값을 쓰지 않고 여기서 다시 계산하는 것은 의도된 이중 구현이다.</b>
 * 완료/홀딩 토글과 상태 스테이징(저장 전 미리보기)이 재조회 없이 즉시 잔액·빵꾸에 반영돼야 하는데,
 * 서버 요약은 조회 시점 파라미터로만 계산되므로 그 역할을 대신할 수 없다.
 *
 * <p><b>[주의 — 백엔드와 의도적으로 다른 점]</b> 백엔드 getCashFlow 는 요약(예정 합계·최저·빵꾸)을
 * 조회 구간 [from, to] 안의 행으로 제한하지만, 이 화면은 <b>전 기간 기준</b>으로 계산한다
 * (화면에도 "표 필터와 무관하게 전 기간·전사 기준"이라고 명시돼 있다). 이유:
 *   · 서버는 구간 이전의 지연 예정분도 함께 내려주고(findTimeline 에 하한이 없다) 이 표는 그 행들을
 *     그대로 보여준다. 표에 보이는 돈을 합계에서 빼면 화면이 자기모순이 된다.
 *   · runningBalance 는 창과 무관하게 누적되므로, 빵꾸 시점(firstNegativeDate/Key)도 같은 기준이어야
 *     '잔액이 빨간 행'·배너 날짜·'이동하기'가 서로 가리키는 행이 일치한다. 구간으로 자르면
 *     구간 전에 이미 마이너스인 경우 배너가 엉뚱한 행을 짚고, minBalance<0 인데 "부족 시점 없음"
 *     초록 배너가 뜨는 자기모순이 생긴다(실제로 그렇게 고쳤다가 되돌린 이력이 있다).
 * 따라서 <b>구간을 좁히는 화면을 새로 만들 때는 이 함수를 재사용하지 말고 서버 요약값을 쓸 것</b>
 * (DashboardTab 이 그렇게 한다). 넓은 구간에서는 두 계산이 실측상 완전히 일치한다.
 */
function computeCashflow(raw, f, todayStr, keepVisibleIds = new Set()) {
    const {
        includeCompleted, includeHold, deptId,
        apprFrom, apprTo, schedFrom, schedTo, amtMin, amtMax, search, statuses = [],
    } = f;
    // 상태 필터로 '완료'를 고르면 과거 완료도 보여줘야 하므로 완료 표시를 자동 포함한다.
    const effIncludeCompleted = includeCompleted || statuses.includes("COMPLETED");
    const base = raw?.baseBalance || null;
    // 기준일자 — 잔액 시작점이자 '지연'·'완료 실현' 판정의 경계. 없으면 오늘.
    const refDate = base?.baseDate || todayStr;
    const allRows = raw?.rows || [];
    let balance = base?.amount || 0;
    let totalIncome = 0, totalExpense = 0;
    let minBalance = null, minBalanceDate = null, firstNegativeDate = null, firstNegativeKey = null;

    const computed = allRows.map(r => {
        const isDone = r.status === "COMPLETED";
        const holdExcluded = r.status === "HOLD" && !includeHold;
        // 잔액을 움직이는가: 홀딩 제외 아니고 — 미완료거나, 완료여도 기준일자 이후(=기준잔액에 아직 안 잡힌 실현분).
        const moves = !holdExcluded && (!isDone || (!!r.scheduledDate && r.scheduledDate > refDate));
        if (moves) {
            if (r.flowType === "INCOME") balance += r.amount; else balance -= r.amount;
            if (minBalance == null || balance < minBalance) { minBalance = balance; minBalanceDate = r.scheduledDate; }
            if (balance < 0 && firstNegativeDate == null) { firstNegativeDate = r.scheduledDate; firstNegativeKey = r.rowKey; }
        }
        // 엑셀처럼 "모든 행"이 그 시점의 잔액을 보여준다. 움직이지 않는 행(홀딩·기준일 이전 완료)은 직전 잔액 그대로.
        const runningBalance = balance;
        // '예정' 합계 — 미완료(pending)만
        if (!isDone && !holdExcluded) {
            if (r.flowType === "INCOME") totalIncome += r.amount; else totalExpense += r.amount;
        }
        // 지연 = 완료 아님 && 예정일이 '오늘' 이전. (기준일자refDate 가 아니라 오늘 기준 —
        // 서버 CashPlanResponse.overdue·지출예정보고서·대시보드가 모두 오늘 기준이라, 마감이 며칠 밀리면
        // 같은 행이 탭마다 지연 여부가 달라 보였다. 판정 기준을 오늘로 단일화한다.)
        const overdue = !isDone && !!r.scheduledDate && r.scheduledDate < todayStr;
        return { ...r, runningBalance, overdue };
    });

    // 표시 필터 — 잔액/KPI 는 위에서 전체 기준으로 이미 계산됨. 여기서는 "무엇을 보여줄지"만 좁힌다.
    const lo = amtMin === "" || amtMin == null ? null : Number(amtMin);
    const hi = amtMax === "" || amtMax == null ? null : Number(amtMax);
    const filtered = computed
        // 완료 건: 기준일자 이후(실현분)는 잔액에 영향 주므로 항상 표시, 그 이전 완료는 '완료 표시' 켰을 때만.
        // 단, 저장 전 스테이징(변경)된 행은 항상 보여준다 — 완료로 바꿔도 목록에서 사라지지 않게(되돌리기 가능).
        .filter(r => keepVisibleIds.has(r.rowKey) || r.status !== "COMPLETED" || effIncludeCompleted || (r.scheduledDate && r.scheduledDate > refDate))
        .filter(r => !statuses.length || statuses.includes(r.status))
        .filter(r => !deptId || String(r.deptId) === String(deptId))
        .filter(r => !apprFrom  || (r.approvalDate && r.approvalDate >= apprFrom))
        .filter(r => !apprTo    || (r.approvalDate && r.approvalDate <= apprTo))
        .filter(r => !schedFrom || (r.scheduledDate && r.scheduledDate >= schedFrom))
        .filter(r => !schedTo   || (r.scheduledDate && r.scheduledDate <= schedTo))
        .filter(r => lo == null || (r.amount ?? 0) >= lo)
        .filter(r => hi == null || (r.amount ?? 0) <= hi)
        .filter(r => searchMatch(search, [r.vendorName, r.title, r.deptName, r.category, r.note, r.authorName, r.channelName]));

    return {
        base, filtered,
        totalScheduledIncome: totalIncome,
        totalScheduledExpense: totalExpense,
        endingBalance: balance,
        minBalance, minBalanceDate, firstNegativeDate, firstNegativeKey,
    };
}

/**
 * 현금 흐름표 (자금 현황) — 조회 + 관리 통합 화면.
 * - 공통: 기준 잔액·빵꾸 경고·예정 입출금 타임라인·잔액 곡선을 조회. 완료/홀딩 포함 계산은 클라이언트 즉시 반영.
 * - 관리 권한(FINANCE_CASHFLOW_MANAGE) 보유 시: 자금계획 등록·수정·삭제 + 고정비 관리까지 이 화면에서 바로.
 *   결재·미수금 '예정'은 저장하지 않고 조회 시점에 원본에서 라이브로 병합돼 자동 표시된다(가져오기 버튼 없음).
 *   라이브 예정 행은 개별 편집·삭제 불가(원천에서 관리)하되, 홀딩/우선지급/지급확정 상태는 여기서 걸 수 있다.
 * - mode 는 레거시 prop(항상 "view"). isRegister 는 더 이상 UI 를 가르지 않는다.
 */
export default function CashFlowTab({ departments = [], projectChannels = [], mode = "view" }) {
    const { hasPerm } = useMyPermissions();
    const canManage = hasPerm("FINANCE_CASHFLOW");
    const isRegister = mode === "register";
    // 현금 흐름표(자금 현황)로 통합 — 조회(잔액·빵꾸)에 더해 관리 권한이 있으면 등록/가져오기/삭제까지 이 화면에서.
    const showCheckbox = canManage;
    const colCount = 10 + (isRegister ? 0 : 1) + (showCheckbox ? 1 : 0);

    const todayStr = formatDate(getToday());
    // 월별/기간 개념 제거(연속 예측). 대신 결재일·송금예정일·금액 범위로 표시를 좁힌다 — 전부 클라이언트 필터.
    const [filters, setFilters] = useState({
        deptId: "",
        includeCompleted: false,
        includeHold: false,
        dimResolved: true,            // 켜면 완료·홀딩 행을 흐리게(예정 건에 시선 집중). 끄면 또렷하게
        apprFrom: "", apprTo: "",     // 결재일 범위
        schedFrom: "", schedTo: "",   // 송금예정일 범위
        amtMin: "", amtMax: "",       // 금액 범위(원)
        statuses: [],                 // 상태 필터(다중) — 비어있으면 전체
        search: "",                   // 통합 검색어(거래처·내용·부서 등)
    });
    const [filterAnchor, setFilterAnchor] = useState(null);   // '필터' 팝오버 앵커
    const [page, setPage]       = useState(0);
    const [size, setSize]       = useState(100);
    const [selected, setSelected] = useState(() => new Set());
    const [raw, setRaw]         = useState(null);   // 기간 전체 원본(완료·홀딩 포함) — 클라이언트에서 재연산
    const [loading, setLoading] = useState(true);
    const [busy, setBusy]       = useState(false);

    // 항목 등록/수정 다이얼로그
    const [planOpen, setPlanOpen]   = useState(false);   // 수정 전용(FormModal)
    const [editing, setEditing]     = useState(null);
    const [form, setForm]           = useState(EMPTY_PLAN);
    const [amountDisplay, setAmountDisplay] = useState("");

    // 통합 등록 폼(예정/확정 토글) — '등록' 버튼이 여는 새 폼
    const [entryOpen, setEntryOpen] = useState(false);
    const [accounts, setAccounts]   = useState([]);   // 확정 등록·완료 시 계좌 선택용

    // 상태 변경은 즉시 저장하지 않고 스테이징 → 하단 저장바에서 일괄 저장(원자적)
    const [pending, setPending] = useState(() => new Map());   // rowKey -> 새 상태(원본과 다른 변경분만)
    const [saving, setSaving]   = useState(false);
    // 저장 시 완료(COMPLETED) 대상 행의 계좌·거래일을 모아 받는 다이얼로그
    const [completeRows, setCompleteRows] = useState(null);    // null=닫힘 | [{ row, bankAccountId, transactionDate }]
    const [rtBalance, setRtBalance]       = useState({});      // 계좌별 실시간 잔액(오늘 미마감분 반영)


    // 고정비 템플릿 다이얼로그
    const [recurringOpen, setRecurringOpen] = useState(false);
    const [templates, setTemplates]         = useState([]);
    const [tplForm, setTplForm]             = useState(null); // null = 목록 모드
    const [tplEditing, setTplEditing]       = useState(null);
    const [tplAmountDisplay, setTplAmountDisplay] = useState("");
    const [genYm, setGenYm] = useState(todayStr.slice(0, 7));

    // 클라이언트 정렬 (컬럼 헤더 클릭) — 기본은 예정일 오름차순(타임라인 순)
    const { sort, toggleSort, sortLabel, sortRows } = useTableSort("scheduledDate", "asc");

    // 서버 조회는 최초 1회(및 CRUD 후) — 넓은 고정 구간 전체를 완료·홀딩 포함해 한 번에 가져온다.
    // 결재일/송금예정일/금액/부서/완료/홀딩/정렬/페이지는 아래 view 에서 클라이언트가 즉시 재연산 → 재조회 없음.
    const load = useCallback(() => {
        setLoading(true);
        const from = formatDate(new Date(Date.now() - LOAD_BACK_DAYS * 86400000));
        const to   = formatDate(new Date(Date.now() + LOAD_FWD_DAYS  * 86400000));
        return financeApi.getCashFlow({ from, to, includeCompleted: true, includeHold: true, page: 0, size: 100000 })
            .then(d => { setRaw(d); setSelected(new Set()); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);
    // 확정 등록·완료 반영용 은행 계좌 목록 (자금일보 계좌)
    useEffect(() => { financeApi.getBankAccounts().then(d => setAccounts(Array.isArray(d) ? d : [])).catch(() => {}); }, []);
    // 완료 처리 다이얼로그가 열리면 오늘 자금일보로 계좌별 실시간 잔액을 가져온다(출금가능액 표시용)
    // 열림/닫힘(boolean)에만 의존 → 모달 안에서 계좌·날짜를 바꿔도 재조회하지 않는다.
    const completeOpen = Boolean(completeRows);
    useEffect(() => {
        if (!completeOpen) return;
        let alive = true;
        financeApi.getDailyCash(todayStr)
            .then(r => { if (alive) setRtBalance(dailyAccountBalances(r)); })
            .catch(() => { if (alive) setRtBalance({}); });
        return () => { alive = false; };
    }, [completeOpen, todayStr]);
    // 표시 조건(필터·정렬)이 바뀌면 첫 페이지로 — 재조회 없이 클라이언트 처리
    useEffect(() => { setPage(0); }, [filters, sort]);

    // 잔액·KPI·표시필터·정렬·페이지를 전부 클라이언트에서 계산 (백엔드와 동일 규칙)
    // 저장 전 스테이징된 상태 변경을 원본에 얹은 "미리보기용" 데이터로 잔액·KPI·빵꾸를 계산한다.
    // (실제 저장은 저장 버튼 → batch. raw 는 그대로 두고 view 만 스테이징을 반영해 즉시 변화가 보이게.)
    const view = useMemo(() => {
        const effRaw = (raw && pending.size > 0)
            ? { ...raw, rows: raw.rows.map(r => (pending.has(r.rowKey) ? { ...r, status: pending.get(r.rowKey) } : r)) }
            : raw;
        const dirtyIds = new Set(pending.keys());
        const c = computeCashflow(effRaw, filters, todayStr, dirtyIds);
        const sorted = sortRows(c.filtered, SORT_ACCESSORS);
        const totalRows = sorted.length;
        const totalPages = Math.max(1, Math.ceil(totalRows / size));
        const safePage = Math.min(Math.max(0, page), totalPages - 1);
        const rows = sorted.slice(safePage * size, safePage * size + size);
        return { ...c, sorted, rows, totalRows, totalPages, page: safePage };
    }, [raw, pending, filters, sortRows, page, size]);

    // 원본(저장된) 상태 — 스테이징 판정/되돌리기 비교용. raw 는 저장 전까지 변하지 않으므로 항상 "원래 상태".
    const origStatusById = useMemo(() => {
        const m = new Map();
        (raw?.rows || []).forEach(r => m.set(r.rowKey, r.status));
        return m;
    }, [raw]);

    const base = view.base;
    const rows = view.rows;

    // 빵꾸(첫 마이너스) 행으로 이동 — 배너 클릭 시 해당 행이 있는 페이지로 넘기고 스크롤·강조
    const firstNegRef = useRef(null);
    const [flashNeg, setFlashNeg] = useState(false);
    const goToFirstNegative = useCallback(() => {
        const key = view.firstNegativeKey;
        if (key == null) return;
        const idx = view.sorted.findIndex(r => r.rowKey === key);
        if (idx < 0) return;   // 현재 표시 필터에서 숨겨져 있으면 이동하지 않음
        setPage(Math.floor(idx / size));
        setFlashNeg(true);
        // 페이지 전환 렌더가 끝난 뒤 해당 행으로 스크롤
        requestAnimationFrame(() => requestAnimationFrame(() => {
            firstNegRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }));
    }, [view.firstNegativeKey, view.sorted, size]);
    useEffect(() => {
        if (!flashNeg) return;
        const t = setTimeout(() => setFlashNeg(false), 2400);
        return () => clearTimeout(t);
    }, [flashNeg]);

    // '필터' 팝오버 — 활성 그룹 수(상태·결재일·송금예정일·금액)와 초기화
    const activeRangeCount = [
        filters.statuses.length > 0,
        filters.apprFrom || filters.apprTo,
        filters.schedFrom || filters.schedTo,
        filters.amtMin !== "" || filters.amtMax !== "",
    ].filter(Boolean).length;
    const clearRangeFilters = () => setFilters(f => ({
        ...f, statuses: [], apprFrom: "", apprTo: "", schedFrom: "", schedTo: "", amtMin: "", amtMax: "",
    }));

    // ─── 항목 CRUD ───────────────────────────────────────────────

    // (등록은 통합 폼 FinanceEntryModal 이 담당 — 아래 FormModal 은 '수정' 전용)
    const openEdit = (row) => {
        // 완료 건·라이브(파생) 행은 여기서 수정 불가. 라이브 행은 원천(결재 문서/미수금 화면)에서 편집한다.
        if (!row.editable) return;
        setEditing(row);
        setForm({
            flowType: row.flowType, title: row.title, amount: row.amount,
            scheduledDate: row.scheduledDate,
            deptId: row.deptId || "",
            channelId: row.channelId || "", channelName: row.channelName || "",
            category: row.category || "", paymentType: row.paymentType || "일반",
            vendorId: row.vendorId || null,
            vendorName: row.vendorName || "", note: row.note || "",
        });
        setAmountDisplay(formatNumberWithCommas(row.amount, { emptyZero: true }));
        setPlanOpen(true);
    };

    const handleSave = async () => {
        const payload = {
            ...form,
            amount: Number(form.amount),
            deptId: form.deptId || null,
            channelId: form.channelId || null,
        };
        const result = await runApiAction({
            request: () => editing
                ? financeApi.updateCashPlan(editing.id, payload)
                : financeApi.createCashPlan(payload),
            successMsg: editing ? "수정되었습니다." : "등록되었습니다.",
            throwOnError: true,
        });
        if (result.ok) { setPlanOpen(false); load(); }
    };

    // 좌측 체크박스 선택 → 일괄 삭제 (완료 건·라이브 파생 행은 선택 불가 = deletable 만)
    const deletableRows = rows => rows.filter(r => r.deletable);

    const toggleSelect = (key) => setSelected(prev => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const toggleSelectAll = (rows) => {
        const targets = deletableRows(rows).map(r => r.rowKey);
        setSelected(prev => prev.size >= targets.length && targets.every(k => prev.has(k))
            ? new Set()
            : new Set(targets));
    };

    const handleBulkDelete = async () => {
        if (selected.size === 0) return;
        if (!(await confirmDialog({ message: `선택한 ${selected.size}건을 삭제하시겠습니까?` }))) return;
        await runApiAction({
            // 삭제 대상은 저장 행뿐(deletable) → rowKey 는 숫자 id 문자열이라 숫자로 변환해 전송
            request: () => financeApi.bulkDeleteCashPlans([...selected].map(Number)),
            successMsg: (d) => `${d?.deleted ?? 0}건이 삭제되었습니다.`
                + (d?.skipped ? ` (완료 건 ${d.skipped}건 제외)` : ""),
            setLoading: setBusy,
        });
        load();
    };

    // 상태 변경 = 즉시 저장하지 않고 스테이징(원본과 다르면 담고, 되돌리면 뺀다). 저장은 하단 저장바에서.
    const handleStatusChange = (row, status) => {
        // 결재/미수금 파생 예정 행은 현금흐름표에서 상태를 바꾸지 않는다(원본 시스템이 관리 → 서버도 거부).
        if (row.virtual) {
            notify("결재/미수금 예정 건은 결재 연동 내역 탭·미수금 수금건에서 처리합니다.", "info");
            return;
        }
        // row.status 는 view 가 스테이징을 얹은 값이라, 스테이징 판정은 반드시 "원본 상태" 기준으로 한다.
        const orig = origStatusById.get(row.rowKey) ?? row.status;
        // 완료 방향: 결재 연동 건은 여기서 완료 불가(서버가 거부 → 배치 전체 롤백되므로 애초에 막는다)
        if (status === "COMPLETED" && row.source === "APPROVAL") {
            notify("결재 연동 건은 결재 연동 내역 탭에서 지출확정하면 자동으로 완료됩니다.", "warning");
            return;
        }
        // 완료 되돌리기: 결재/급여 연동(잠금) 실적은 되돌릴 수 없음(서버가 거부 → 배치 전체 롤백 방지)
        if (orig === "COMPLETED" && status !== "COMPLETED" && (row.source === "APPROVAL" || row.locked)) {
            notify("결재/급여 연동 실적은 완료를 되돌릴 수 없습니다. (연동 시스템에서 관리됩니다)", "warning");
            return;
        }
        setPending(prev => {
            const m = new Map(prev);
            if (status === orig) m.delete(row.rowKey);   // 원래 상태로 되돌리면 변경분에서 제거
            else m.set(row.rowKey, status);
            return m;
        });
    };

    const revertPending = () => setPending(new Map());

    // 저장 클릭 — 완료(COMPLETED) 대상이 있으면 계좌·거래일 모달을 먼저 띄우고, 없으면 바로 일괄 저장
    const handleSaveAll = () => {
        if (pending.size === 0 || saving) return;
        const completes = [];
        for (const [key, status] of pending) {
            if (status !== "COMPLETED") continue;
            const row = (raw?.rows || []).find(r => r.rowKey === key);
            if (row) completes.push({ row, bankAccountId: "", transactionDate: todayStr });
        }
        if (completes.length > 0) { setCompleteRows(completes); return; }  // → 완료 모달 → confirmBatchComplete
        commitSave([]);
    };

    // 실제 일괄 저장 — completedInputs: [{ rowKey, bankAccountId, transactionDate }]. 하나라도 실패하면 서버가 전면 롤백.
    const commitSave = async (completedInputs) => {
        const cmap = new Map((completedInputs || []).map(c => [c.rowKey, c]));
        const items = [];
        for (const [key, status] of pending) {
            const c = cmap.get(key);
            items.push({
                rowKey: key, status,
                bankAccountId: c ? (c.bankAccountId || null) : null,
                transactionDate: c ? (c.transactionDate || null) : null,
            });
        }
        const result = await runApiAction({
            request: () => financeApi.batchChangeCashPlanStatus(items),
            successMsg: `${items.length}건 저장되었습니다.`,
            setLoading: setSaving,
        });
        if (result.ok) {
            setPending(new Map());
            setCompleteRows(null);
            load();   // 저장 반영(라이브 행 상태 오버라이드·실적 전환) 재조회
        }
        // 실패 시: 서버가 전면 롤백 → pending 유지, 사용자가 고쳐서 재시도.
    };

    // 완료 모달 저장 — 각 완료 행의 계좌·거래일을 확정한 뒤 일괄 저장으로 넘긴다
    const confirmBatchComplete = () => {
        const inputs = (completeRows || []).map(c => ({
            rowKey: c.row.rowKey, bankAccountId: c.bankAccountId || null, transactionDate: c.transactionDate || todayStr,
        }));
        commitSave(inputs);
    };

    // ─── 고정비 생성 ─────────────────────────────────────────────

    const generateRecurring = () => {
        const [y, m] = genYm.split("-").map(Number);
        return runApiAction({
            request: () => financeApi.generateRecurring(y, m),
            successMsg: (d) => `${y}년 ${m}월 고정비 ${d?.created ?? 0}건을 생성했습니다.`,
            setLoading: setBusy,
            onSuccess: () => { load(); },
        });
    };

    // 기준 잔액은 자금일보 최신 일마감 총액에서 서버가 자동 파생한다(읽기 전용) — 별도 설정·연동 액션 없음.

    // ─── 고정비 템플릿 ───────────────────────────────────────────

    const loadTemplates = () => financeApi.getRecurringTemplates().then(setTemplates).catch(() => {});
    const openRecurring = () => { setTplForm(null); setTplEditing(null); loadTemplates(); setRecurringOpen(true); };

    const openTplCreate = () => { setTplEditing(null); setTplForm(EMPTY_TEMPLATE); setTplAmountDisplay(""); };
    const openTplEdit = (t) => {
        setTplEditing(t);
        setTplForm({
            flowType: t.flowType, title: t.title, amount: t.amount, payDay: t.payDay,
            startDate: t.startDate || "", endDate: t.endDate || "",   // 유효기간 보존(누락 시 서버가 삭제함)
            vendorId: t.vendorId || null, vendorName: t.vendorName || "",
            category: t.category || "고정비/관리비", note: t.note || "", active: t.active,
        });
        setTplAmountDisplay(formatNumberWithCommas(t.amount, { emptyZero: true }));
    };

    const saveTemplate = async () => {
        // 빈 문자열은 서버 LocalDate 파싱이 실패하므로 null 로 보낸다(= 무기한).
        const payload = {
            ...tplForm,
            amount: Number(tplForm.amount), payDay: Number(tplForm.payDay),
            startDate: tplForm.startDate || null, endDate: tplForm.endDate || null,
        };
        const result = await runApiAction({
            request: () => tplEditing
                ? financeApi.updateRecurringTemplate(tplEditing.id, payload)
                : financeApi.createRecurringTemplate(payload),
            successMsg: tplEditing ? "템플릿이 수정되었습니다." : "템플릿이 등록되었습니다.",
            throwOnError: true,
        });
        if (result.ok) { setTplForm(null); setTplEditing(null); loadTemplates(); }
    };

    const deleteTemplate = async (t) => {
        if (!(await confirmDialog({ message: `"${t.title}" 템플릿을 삭제하시겠습니까? (이미 생성된 월 항목은 유지됩니다)` }))) return;
        await financeApi.deleteRecurringTemplate(t.id);
        loadTemplates();
    };

    const handleAmountInput = (raw, setter, field, setDisplay) => {
        const { value, display } = toNumericInputState(raw, { emptyZero: true });
        setter(f => ({ ...f, [field]: value }));
        setDisplay(display);
    };

    // ─── 렌더 ────────────────────────────────────────────────────

    return (
        <Box>

            {/* 기준 잔액 — 자금일보 최신 일마감 총액에서 자동 파생(읽기 전용). 이 기준일자가 '지연' 판정 기준이 된다. */}
            <Paper sx={{ ...ds.cardPadded, mb: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                <Typography sx={{ ...ds.typography.label, mr: 0.5 }}>기준 잔액</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f766e" }}>
                    {base ? fmtKRWFull(base.amount) : "미설정"}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                    {base
                        ? `${base.baseDate} 자금일보 마감 총액 · 이 날짜 이전 미완료는 '지연'`
                        : "자금일보에서 일마감을 하면 기준 잔액이 자동 계산됩니다"}
                </Typography>
            </Paper>

            {/* KPI 카드 — 예정 입/출금·기말 예상 */}
            {!isRegister && <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={ds.typography.label}>예정 입금</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: ds.finance.income, mt: 0.5 }}>
                        {fmtKRWFull(view.totalScheduledIncome ?? 0)}
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography sx={ds.typography.label}>예정 출금</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 24, fontWeight: 800, color: ds.finance.expense, mt: 0.5 }}>
                        {fmtKRWFull(view.totalScheduledExpense ?? 0)}
                    </Typography>
                </Paper>
                <Paper sx={{ ...ds.cardPadded, flex: 1 }}>
                    <Typography sx={ds.typography.label}>기말 예상 잔액</Typography>
                    <Typography sx={{
                        fontSize: 24, fontWeight: 800, mt: 0.5,
                        color: (view.endingBalance ?? 0) < 0 ? ds.finance.expense : ds.finance.balance,
                    }}>
                        {fmtKRWFull(view.endingBalance ?? 0)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                        최저 {view.minBalance != null ? fmtKRWFull(view.minBalance) : "—"}
                    </Typography>
                </Paper>
            </Box>}
            {/* KPI 는 표 필터(팀·상태·기간·검색) 이전의 전체 타임라인으로 계산된다(잔액 곡선의 연속성 때문에 전체 기준 유지가 필요).
                필터를 걸어도 숫자가 안 바뀌는 것처럼 보여 오해를 낳으므로, 전 기간·전사 기준임을 명시한다. */}
            {!isRegister && <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: -2, mb: 2.5 }}>
                ※ 위 KPI(예정 입·출금·기말/최저 잔액)는 표 필터와 무관하게 전 기간·전사 기준으로 계산됩니다.
            </Typography>}

            {/* 빵꾸(마이너스 잔액) 경고 — 클릭하면 표에서 처음 마이너스 되는 행으로 이동 */}
            {view.firstNegativeDate ? (
                <Alert severity="error" icon=''
                    onClick={goToFirstNegative}
                    role="button"
                    sx={{
                        borderRadius: 1, fontWeight: 600, mb: 2, cursor: "pointer",
                        "& .MuiAlert-message": { width: "100%" },
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <b>{view.firstNegativeDate}</b> 부터 잔액이 마이너스가 됩니다 / 최저 {fmtKRWFull(view.minBalance)} ({view.minBalanceDate}) → 이동하기.
                        </Box>
                    </Box>
                </Alert>
            ) : view.minBalance != null ? (
                <Alert severity="success" sx={{ borderRadius: 1.5, mb: 2 }}>
                    향후 자금 부족 시점이 없습니다. 최저 예상 잔액 {fmtKRWFull(view.minBalance)} ({view.minBalanceDate}).
                </Alert>
            ) : null}

            {/* 필터 + 액션 */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
                {/* 결재일·송금예정일·금액 범위를 한 팝오버로 (현금 흐름표는 월/기간 개념 대신 이 필터로 좁힌다) */}
                <Button size="small" variant="outlined"
                    startIcon={<FilterListRoundedIcon sx={{ fontSize: 16 }} />}
                    onClick={e => setFilterAnchor(e.currentTarget)}
                    sx={{
                        textTransform: "none", fontSize: 12, height: 37,
                        borderColor: activeRangeCount ? "#2563eb" : "rgba(0,0,0,0.23)",
                        color: activeRangeCount ? "#2563eb" : "#475569",
                        bgcolor: activeRangeCount ? "#eff6ff" : "transparent",
                    }}>
                    필터{activeRangeCount ? ` ${activeRangeCount}` : ""}
                </Button>
                <SearchField value={filters.search}
                    onChange={v => setFilters(f => ({ ...f, search: v }))}
                    placeholder="거래처·내용·부서 검색" width={220} />
                <Box sx={{ width: 150 }}>
                    <ComboBox placeholder="전체 팀" clearable={false}
                        options={[{ value: "", label: "전체 팀" }, ...departments.map(d => ({ value: d.deptId, label: d.deptName }))]}
                        value={filters.deptId}
                        onChange={v => setFilters(f => ({ ...f, deptId: v }))} />
                </Box>
                <Tooltip title="기준일자 이전의 지난 완료 건까지 표시합니다. (기준일자 이후 완료분은 실제 들어온/나간 돈이라 항상 표시되고 잔액에 반영됩니다)">
                    <FormControlLabel
                        control={<Switch size="small" checked={filters.includeCompleted}
                            onChange={e => setFilters(f => ({ ...f, includeCompleted: e.target.checked }))} />}
                        label={<Typography sx={{ fontSize: 12 }}>지난 완료 표시</Typography>} />
                </Tooltip>
                {!isRegister && (
                    <Tooltip title="켜면 홀딩 항목도 잔액 계산에 포함해 시뮬레이션합니다">
                        <FormControlLabel
                            control={<Switch size="small" checked={filters.includeHold}
                                onChange={e => setFilters(f => ({ ...f, includeHold: e.target.checked }))} />}
                            label={<Typography sx={{ fontSize: 12 }}>홀딩 포함 계산</Typography>} />
                    </Tooltip>
                )}
                <Tooltip title="켜면 완료·홀딩 행을 흐리게 표시해 예정 건에 시선이 가게 합니다. 끄면 모든 행을 또렷하게 봅니다. (표시 여부와 무관 — 현금흐름표는 항상 전체 표시)">
                    <FormControlLabel
                        control={<Switch size="small" checked={filters.dimResolved}
                            onChange={e => setFilters(f => ({ ...f, dimResolved: e.target.checked }))} />}
                        label={<Typography sx={{ fontSize: 12 }}>완료·홀딩 흐리게</Typography>} />
                </Tooltip>
                <Box sx={{ flex: 1 }} />
                {/* N개씩 보기 — 표 우측 상단 고정(공용 컨트롤) */}
                <TableSizeSelect value={size} onChange={(n) => { setSize(n); setPage(0); }} />
                {canManage && <>
                    {/* 결재·미수금은 이제 자동으로 라이브 반영됨 — '가져오기' 버튼 없이 실시간 표시 */}
                    <ActionButton variant="secondary"
                        startIcon={<EventRepeatRoundedIcon sx={{ fontSize: 15 }} />}
                        onClick={openRecurring} label="고정비 관리" sx={{ fontSize: 12 }} />
                    <ActionButton variant="add" onClick={() => setEntryOpen(true)} label="등록" />
                </>}
            </Box>

            {/* 필터 팝오버 — 결재일 · 송금예정일 · 금액 범위를 한 곳에서 */}
            <Popover
                open={Boolean(filterAnchor)} anchorEl={filterAnchor}
                onClose={() => setFilterAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <Box sx={{ p: 2, width: 320 }}>
                    <Stack spacing={1.8}>
                        {/* 상태 — 다중 선택 토글칩(비어있으면 전체). '완료' 선택 시 과거 완료도 자동 표시됨. */}
                        <Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569", mb: 0.6 }}>
                                상태
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                                {STATUS_OPTIONS.map(o => {
                                    const on = filters.statuses.includes(o.value);
                                    return (
                                        <Chip key={o.value} label={o.label} size="small"
                                            onClick={() => setFilters(f => ({
                                                ...f,
                                                statuses: on
                                                    ? f.statuses.filter(s => s !== o.value)
                                                    : [...f.statuses, o.value],
                                            }))}
                                            sx={{
                                                cursor: "pointer", fontSize: 12, fontWeight: 700, height: 26,
                                                color: on ? o.color : "#94a3b8",
                                                bgcolor: on ? o.bg : "transparent",
                                                border: `1px solid ${on ? o.color : "#e2e8f0"}`,
                                                "&:hover": { bgcolor: on ? o.bg : "#f8fafc" },
                                            }} />
                                    );
                                })}
                            </Box>
                        </Box>
                        {[
                            { label: "결재일",     type: "date", fromKey: "apprFrom",  toKey: "apprTo" },
                            { label: "송금예정일", type: "date", fromKey: "schedFrom", toKey: "schedTo" },
                            { label: "금액",       type: "amount", fromKey: "amtMin",  toKey: "amtMax" },
                        ].map(g => (
                            <Box key={g.label}>
                                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#475569", mb: 0.6 }}>
                                    {g.label}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                    <TextField
                                        size="small" sx={{ flex: 1 }}
                                        type={g.type === "date" ? "date" : "number"}
                                        placeholder={g.type === "amount" ? "최소" : undefined}
                                        value={filters[g.fromKey]}
                                        onChange={e => setFilters(f => ({ ...f, [g.fromKey]: e.target.value }))}
                                        InputProps={g.type === "amount"
                                            ? { endAdornment: <InputAdornment position="end">원</InputAdornment> } : undefined}
                                        inputProps={g.type === "amount" ? { min: 0, inputMode: "numeric" } : undefined}
                                    />
                                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>~</Typography>
                                    <TextField
                                        size="small" sx={{ flex: 1 }}
                                        type={g.type === "date" ? "date" : "number"}
                                        placeholder={g.type === "amount" ? "최대" : undefined}
                                        value={filters[g.toKey]}
                                        onChange={e => setFilters(f => ({ ...f, [g.toKey]: e.target.value }))}
                                        InputProps={g.type === "amount"
                                            ? { endAdornment: <InputAdornment position="end">원</InputAdornment> } : undefined}
                                        inputProps={g.type === "amount" ? { min: 0, inputMode: "numeric" } : undefined}
                                    />
                                </Box>
                            </Box>
                        ))}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pt: 0.5 }}>
                            <Button size="small" onClick={clearRangeFilters}
                                disabled={activeRangeCount === 0}
                                sx={{ fontSize: 12, textTransform: "none", color: "#64748b" }}>
                                초기화
                            </Button>
                            <Button size="small" variant="contained" onClick={() => setFilterAnchor(null)}
                                sx={{ fontSize: 12, textTransform: "none" }}>
                                닫기
                            </Button>
                        </Box>
                    </Stack>
                </Box>
            </Popover>

            {/* 선택 삭제 바 — 등록 모드 전용 */}
            {showCheckbox && selected.size > 0 && (
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 1.5, mb: 1.5,
                    px: 2, py: 1, bgcolor: "#fef2f2", borderRadius: 1.5,
                }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#dc2626" }}>
                        {selected.size}건 선택됨
                    </Typography>
                    <ActionButton variant="secondary" loading={busy} onClick={handleBulkDelete}
                        startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                        label="선택 삭제" sx={{ fontSize: 12, color: "#dc2626" }} />
                    <Chip label="선택 해제" size="small" onClick={() => setSelected(new Set())}
                        sx={{ height: 22, fontSize: 11, cursor: "pointer" }} />
                </Box>
            )}

            {/* 타임라인 테이블 — 행 클릭 = 수정, 좌측 체크박스 = 삭제 선택 */}
            <Paper sx={ds.card}>
                {loading && <LoadingDots />}
                {!loading && <ResizableTable size="small" sx={ds.tableSx}>
                    <TableHead>
                        <TableRow>
                            {showCheckbox && (
                                <TableCell padding="checkbox">
                                    <Checkbox size="small"
                                        checked={deletableRows(rows).length > 0
                                            && deletableRows(rows).every(r => selected.has(r.rowKey))}
                                        indeterminate={selected.size > 0
                                            && !deletableRows(rows).every(r => selected.has(r.rowKey))}
                                        onChange={() => toggleSelectAll(rows)} />
                                </TableCell>
                            )}
                            <TableCell align="center" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("approvalDate")}>{sortLabel("approvalDate", "결재일")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("scheduledDate")}>{sortLabel("scheduledDate", "입/출금일(예정포함)")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("vendorName")}>{sortLabel("vendorName", "거래처")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("deptName")}>{sortLabel("deptName", "부서")}</TableCell>
                            <TableCell sx={{ cursor: "pointer" }} onClick={() => toggleSort("title")}>{sortLabel("title", "행사명")}</TableCell>
                            <TableCell sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("category")}>{sortLabel("category", "거래내용")}</TableCell>
                            <TableCell align="right" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("income")}>{sortLabel("income", "입금액")}</TableCell>
                            <TableCell align="right" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("expense")}>{sortLabel("expense", "출금액")}</TableCell>
                            <TableCell align="center" sx={{ cursor: "pointer" }} onClick={() => toggleSort("status")}>{sortLabel("status", "상태")}</TableCell>
                            <TableCell>비고</TableCell>
                            {!isRegister && <TableCell align="right" sx={{ cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => toggleSort("runningBalance")}>{sortLabel("runningBalance", "잔액")}</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={colCount} align="center" sx={{ py: 4, color: "#94a3b8" }}>
                                    {canManage
                                        ? "해당 기간에 자금계획이 없습니다. 위 [등록]으로 시작하세요. (결재·미수금은 자동으로 표시됩니다)"
                                        : "해당 기간에 자금계획이 없습니다."}
                                </TableCell>
                            </TableRow>
                        ) : rows.map(row => {
                            // 저장 전 스테이징된 상태를 우선 표시(변경분). 원본 상태는 row.status.
                            const effStatus = pending.has(row.rowKey) ? pending.get(row.rowKey) : row.status;
                            const isDirty = pending.has(row.rowKey);
                            const si = statusInfo(effStatus);
                            const isHold = row.status === "HOLD";
                            const isDone = row.status === "COMPLETED";
                            const negRow = row.runningBalance != null && row.runningBalance < 0;
                            // 잔액이 처음 마이너스가 되는 그 행만 빨강 강조 (정렬해도 위치 안 흔들리게 rowKey 로 판정)
                            const markFirst = !isRegister && row.rowKey === view.firstNegativeKey;
                            const clickable = showCheckbox && row.editable;
                            return (
                                <TableRow key={row.rowKey} hover={clickable}
                                    ref={markFirst ? firstNegRef : undefined}
                                    onClick={() => clickable && openEdit(row)}
                                    sx={{
                                        opacity: (isHold || isDone) && filters.dimResolved ? 0.55 : 1,
                                        // 변경(스테이징)된 행은 붉게 + 좌측 빨간 바로 확인
                                        bgcolor: isDirty ? "#fef2f2" : markFirst ? "#fef2f2" : undefined,
                                        boxShadow: isDirty ? "inset 3px 0 0 #dc2626" : undefined,
                                        cursor: clickable ? "pointer" : "default",
                                        ...(markFirst && flashNeg ? {
                                            outline: "2px solid #dc2626",
                                            outlineOffset: "-2px",
                                            transition: "outline 0.15s ease",
                                        } : {}),
                                    }}>
                                    {showCheckbox && (
                                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                            <Checkbox size="small" disabled={!row.deletable}
                                                checked={selected.has(row.rowKey)}
                                                onChange={() => toggleSelect(row.rowKey)} />
                                        </TableCell>
                                    )}
                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12, color: "#64748b" }}>
                                        {row.approvalDate || "—"}
                                    </TableCell>
                                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                                        {row.scheduledDate}
                                        {row.overdue && (
                                            <Chip label="지연" size="small" sx={{
                                                ml: 0.5, height: 18, fontSize: 10, fontWeight: 700,
                                                bgcolor: "#fef2f2", color: "#dc2626",
                                            }} />
                                        )}
                                    </TableCell>
                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{row.vendorName || "—"}</TableCell>
                                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>{row.deptName || "—"}</TableCell>
                                    <TableCell align="center" sx={{ maxWidth: 300 }}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3 }}>
                                                {row.title}
                                            </Typography>
                                            {row.source !== "MANUAL" && (
                                                <Chip label={row.sourceLabel} size="small" sx={{
                                                    height: 17, fontSize: 10, bgcolor: "#eef2ff", color: "#4f46e5",
                                                }} />
                                            )}
                                        </Box>
                                        {row.channelName && (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.4, mt: 0.2 }}>
                                                <FolderSpecialRoundedIcon sx={{ fontSize: 12, color: "#2563eb" }} />
                                                <Typography sx={{ fontSize: 11, color: "#64748b" }}>{row.channelName}</Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 12 }}>{row.category || "—"}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: ds.finance.income, whiteSpace: "nowrap" }}>
                                        {row.flowType === "INCOME" ? (
                                            <>
                                                +{fmtKRWFull(row.amount)}
                                                {/* 미수금 파생 행: 잔여 미수금이 잔액 반영값, 청구/수금은 표시용 */}
                                                {row.source === "RECEIVABLE" && row.collectedAmount > 0 && (
                                                    <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: "#94a3b8", mt: 0.2, whiteSpace: "nowrap" }}>
                                                        청구 {fmtKRWFull(row.billingAmount)} · 수금 {fmtKRWFull(row.collectedAmount)}
                                                    </Typography>
                                                )}
                                            </>
                                        ) : ""}
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700, color: ds.finance.expense, whiteSpace: "nowrap" }}>
                                        {row.flowType === "EXPENSE" ? `−${fmtKRWFull(row.amount)}` : ""}
                                    </TableCell>
                                    <TableCell align="center" onClick={e => e.stopPropagation()}>
                                        {canManage ? (
                                            <Select size="small" value={effStatus} variant="standard" disableUnderline
                                                onChange={e => handleStatusChange(row, e.target.value)}
                                                sx={{
                                                    fontSize: 12, fontWeight: 700, color: si.color,
                                                    bgcolor: si.bg, borderRadius: 1, px: 1, py: 0.2,
                                                    "& .MuiSelect-select": { py: 0.2 },
                                                }}>
                                                {statusOptionsFor(row, effStatus).map(o => (
                                                    <MenuItem key={o.value} value={o.value} sx={{ fontSize: 12 }}>
                                                        {o.label}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        ) : (
                                            <Chip label={si.label} size="small"
                                                sx={{ bgcolor: si.bg, color: si.color, fontWeight: 600 }} />
                                        )}
                                    </TableCell>
                                    <TableCell align="center" sx={{ maxWidth: 160 }}>
                                        <Typography noWrap sx={{ fontSize: 12, color: "#64748b" }} title={row.note}>
                                            {row.note || "—"}
                                        </Typography>
                                    </TableCell>
                                    {!isRegister && (
                                        <TableCell align="right" sx={{
                                            fontWeight: 800, whiteSpace: "nowrap",
                                            color: negRow ? ds.finance.expense : ds.finance.balance,
                                        }}>
                                            {row.runningBalance != null ? fmtKRWFull(row.runningBalance) : "—"}
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </ResizableTable>}

                {/* 페이지 이동 — 표 아래 가운데(전 시스템 공통). */}
                {!loading && view.totalRows > 0 && (
                    <TablePaginator
                        totalPages={view.totalPages}
                        total={view.totalRows}
                        pageSize={size}
                        page={view.page + 1}
                        onPageChange={(p) => setPage(p - 1)}
                    />
                )}

            </Paper>

            {/* 하단 고정 저장바 — 상태 변경(스테이징)이 있을 때만. 변경 건수 실시간 표시 + 일괄 저장/되돌리기 */}
            {canManage && pending.size > 0 && (
                <Box sx={{
                    // [zIndex] 챗봇 FAB·대화창이 zIndex 1300 으로 우하단에 떠 있어, 예전엔 이 바(20)가 그 아래
                    //   깔렸다. 저장 버튼 오른쪽을 누르면 저장 대신 AI 비서가 열렸고, 그대로 탭을 옮기면
                    //   스테이징(pending) 변경분이 통째로 버려졌다. 미저장 변경이 있을 때만 뜨는 바이므로
                    //   떠 있는 보조 UI보다 위에 두는 게 맞다.
                    // [pr] z 로 눌러도 겹쳐 보이는 건 여전하므로, 버튼이 FAB 자리(우측 24~80px)에 들어가지
                    //   않도록 오른쪽 여백을 미리 비워 둔다.
                    position: "sticky", bottom: 0, zIndex: 1400, mt: 2,
                    display: "flex", alignItems: "center", gap: 1.25,
                    px: 2, pr: { xs: 2, sm: 12 }, py: 1.25,
                    bgcolor: "#fff", borderTop: "2px solid #dc2626", borderRadius: "10px 10px 0 0",
                    boxShadow: "0 -6px 16px rgba(15,23,42,0.10)",
                }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#dc2626" }} />
                    <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: "#dc2626" }}>
                        {pending.size}건 변경됨
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>— 저장해야 반영됩니다</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Chip label="되돌리기" size="small" onClick={revertPending}
                        disabled={saving}
                        sx={{ height: 28, fontSize: 12.5, cursor: "pointer" }} />
                    <ActionButton variant="add" loading={saving}
                        startIcon={<SaveRoundedIcon sx={{ fontSize: 16 }} />}
                        onClick={handleSaveAll} label={`저장 (${pending.size}건)`} />
                </Box>
            )}

            {/* 통합 등록 폼 — 예정/확정 토글 (현금흐름표=예정 기본) */}
            <FinanceEntryModal open={entryOpen} onClose={() => setEntryOpen(false)} onSaved={load}
                mode0="SCHEDULED" departments={departments} projectChannels={projectChannels} accounts={accounts} />

            {/* 완료 처리 — 저장 시 완료 대상 행들의 계좌·거래일을 한 번에 지정 (자금일보 자동 반영) */}
            <FormModal open={Boolean(completeRows)} onClose={() => { if (!saving) setCompleteRows(null); }}
                title="완료 처리" subtitle={completeRows ? `완료 ${completeRows.length}건 — 계좌·거래일을 지정하세요` : ""}
                maxWidth="sm" onSave={confirmBatchComplete} saveLabel={`저장 (${pending.size}건)`}
                saveDisabled={saving || !(completeRows || []).every(c => c.bankAccountId && c.transactionDate)}>
                <Stack spacing={1.5}>
                    <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>
                        완료하면 실적(확정 매출/지출)에 기록되고, 선택한 계좌의 자금일보에 자동 반영됩니다.
                        나머지 상태 변경도 함께 저장되며, 하나라도 실패하면 전체가 취소됩니다.
                    </Typography>
                    {(completeRows || []).map((c, idx) => {
                        const acc = accounts.find(a => String(a.id) === String(c.bankAccountId));
                        const bal = acc ? (rtBalance[acc.id] != null ? rtBalance[acc.id] : (acc.currentBalance ?? 0)) : null;
                        const exp = c.row.flowType === "EXPENSE";
                        const after = bal != null ? (exp ? bal - c.row.amount : bal + c.row.amount) : null;
                        const neg = exp && after != null && after < 0;
                        const setC = (patch) => setCompleteRows(prev => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));
                        return (
                            <Box key={c.row.rowKey} sx={{ p: 1.25, border: "1px solid #e2e8f0", borderRadius: 1 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.75 }}>
                                    {c.row.title} · <span style={{ color: exp ? ds.finance.expense : ds.finance.income }}>
                                        {exp ? "−" : "+"}{fmtKRWFull(c.row.amount)}</span>
                                </Typography>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                                    <Box sx={{ flex: 1.5 }}>
                                        <ComboBox placeholder="입출금 계좌"
                                            options={accounts.map(a => ({
                                                value: a.id,
                                                label: `${a.alias || a.bankName} · ${fmtKRWFull(rtBalance[a.id] != null ? rtBalance[a.id] : (a.currentBalance ?? 0))}`,
                                            }))}
                                            value={c.bankAccountId}
                                            onChange={v => setC({ bankAccountId: v })} />
                                    </Box>
                                    <TextField size="small" type="date" value={c.transactionDate}
                                        onChange={e => setC({ transactionDate: e.target.value })}
                                        sx={{ width: { xs: "100%", sm: 150 } }} />
                                </Stack>
                                {c.bankAccountId && after != null && (
                                    <Typography sx={{ mt: 0.6, fontSize: 11.5, fontWeight: 700, color: neg ? ds.finance.expense : "#0f766e" }}>
                                        {exp ? "출금 후 잔액 " : "입금 후 잔액 "}{fmtKRWFull(after)}{neg ? " · 잔액 부족" : ""}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Stack>
            </FormModal>

            {/* 항목 수정 (등록은 위 통합 폼) */}
            <FormModal open={planOpen} onClose={() => setPlanOpen(false)}
                title="자금계획 수정" maxWidth="sm"
                onSave={handleSave}
                saveDisabled={!form.title || !form.amount || !form.scheduledDate}>
                <Stack spacing={2}>
                    <FormField label="구분" required>
                        <ComboBox clearable={false}
                            options={[{ value: "EXPENSE", label: "출금 (지출 예정)" }, { value: "INCOME", label: "입금 (수입 예정)" }]}
                            value={form.flowType}
                            onChange={v => setForm(f => ({ ...f, flowType: v }))} />
                    </FormField>
                    <FormField label="내용" required>
                        <TextField size="small" value={form.title}
                            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                            placeholder="행사명·거래 내용을 입력하세요" />
                    </FormField>
                    <FormField label="금액" required>
                        <TextField size="small" value={amountDisplay}
                            onChange={e => handleAmountInput(e.target.value, setForm, "amount", setAmountDisplay)}
                            InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                            inputProps={{ inputMode: "numeric" }} placeholder="0" />
                    </FormField>
                    <FormField label={form.flowType === "INCOME" ? "입금 예정일" : "송금 예정일"} required>
                        <TextField fullWidth size="small" type="date" value={form.scheduledDate}
                            onChange={e => setForm(f => ({ ...f, scheduledDate: e.target.value }))} />
                    </FormField>
                    <FormField label="팀">
                        <ComboBox placeholder="선택 안 함"
                            options={departments.map(d => ({ value: d.deptId, label: d.deptName }))}
                            value={form.deptId}
                            onChange={v => setForm(f => ({ ...f, deptId: v }))} />
                    </FormField>
                    <FormField label="프로젝트 채널">
                        <ComboBox placeholder="선택 안 함"
                            options={projectChannels.map(c => ({ value: c.id, label: c.name }))}
                            value={form.channelId}
                            onChange={v => {
                                const ch = projectChannels.find(c => String(c.id) === String(v));
                                setForm(f => ({ ...f, channelId: v, channelName: ch ? ch.name : "" }));
                            }} />
                    </FormField>
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <FormField label="거래내용 구분" sx={{ flex: 2, minWidth: 0 }}>
                            <ComboBox freeSolo placeholder="프로젝트 / 사업소득 / 고정비 등"
                                options={CATEGORY_SUGGESTIONS}
                                value={form.category}
                                onChange={v => setForm(f => ({ ...f, category: v }))} />
                        </FormField>
                        <FormField label="결제 유형" sx={{ flex: 1, minWidth: 110 }}>
                            <ComboBox freeSolo placeholder="일반 / 고정비"
                                options={PAYMENT_TYPES}
                                value={form.paymentType || "일반"}
                                onChange={v => setForm(f => ({ ...f, paymentType: v }))} />
                        </FormField>
                    </Box>
                    <FormField label="거래처">
                        <VendorAutocomplete freeSolo value={form.vendorId} initialName={form.vendorName}
                            onChange={(id, vendorOrText) => {
                                if (typeof vendorOrText === "string") {
                                    setForm(f => ({ ...f, vendorId: null, vendorName: vendorOrText }));
                                } else if (vendorOrText) {
                                    setForm(f => ({ ...f, vendorId: id, vendorName: vendorOrText.name }));
                                } else {
                                    setForm(f => ({ ...f, vendorId: null, vendorName: "" }));
                                }
                            }}
                            label={null} helperText="등록된 거래처에서 선택하거나 그대로 입력하세요" />
                    </FormField>
                    <FormField label="비고">
                        <TextField size="small" multiline rows={2} value={form.note}
                            onChange={e => setForm(f => ({ ...f, note: e.target.value }))} />
                    </FormField>
                </Stack>
            </FormModal>

            {/* 고정비 템플릿 관리 */}
            <FormModal open={recurringOpen} onClose={() => setRecurringOpen(false)}
                title="고정비(관리비) 템플릿 관리" maxWidth="md" cancelLabel="닫기">
                {tplForm === null ? (
                    <Box>
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
                            <TextField size="small" type="month" value={genYm}
                                onChange={e => setGenYm(e.target.value)} sx={{ width: 160 }} />
                            <ActionButton variant="secondary" loading={busy} onClick={generateRecurring}
                                startIcon={<EventRepeatRoundedIcon sx={{ fontSize: 15 }} />}
                                label="해당 월 고정비 생성" sx={{ fontSize: 12 }} />
                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                이미 생성된 템플릿은 건너뜁니다. 카드대금처럼 금액이 바뀌는 건 생성 후 개별 수정하세요.
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            <ActionButton variant="add" onClick={openTplCreate} label="템플릿 추가"
                                sx={{ fontSize: 12 }} />
                        </Box>
                        <ResizableTable size="small" sx={ds.tableSx}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>지급일</TableCell>
                                    <TableCell>내용</TableCell>
                                    <TableCell>거래처</TableCell>
                                    <TableCell align="right">금액</TableCell>
                                    <TableCell>활성</TableCell>
                                    <TableCell align="center">관리</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {templates.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: "#94a3b8" }}>
                                            등록된 고정비 템플릿이 없습니다. (예: 임대료, 대출이자, 카드대금, 4대보험)
                                        </TableCell>
                                    </TableRow>
                                ) : templates.map(t => (
                                    <TableRow key={t.id} sx={{ opacity: t.active ? 1 : 0.45 }}>
                                        <TableCell>매월 {t.payDay}일</TableCell>
                                        <TableCell>{t.title}</TableCell>
                                        <TableCell>{t.vendorName || "—"}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                                            {t.flowType === "INCOME" ? "+" : "−"}{fmtKRWFull(t.amount)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={t.active ? "활성" : "중지"} size="small" sx={{
                                                height: 20, fontSize: 11,
                                                bgcolor: t.active ? "#f0fdf4" : "#f1f5f9",
                                                color: t.active ? "#16a34a" : "#94a3b8",
                                            }} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                                <IconButton size="small" onClick={() => openTplEdit(t)}>
                                                    <EditIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => deleteTemplate(t)}>
                                                    <DeleteIcon sx={{ fontSize: 15 }} />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </ResizableTable>
                    </Box>
                ) : (
                    <Stack spacing={2}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            {tplEditing ? "템플릿 수정" : "새 템플릿"}
                        </Typography>
                        <FormField label="내용" required>
                            <TextField size="small" value={tplForm.title}
                                onChange={e => setTplForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="예: 문정동 월세 / 농협 대출이자" />
                        </FormField>
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="예상 금액" required>
                                <TextField size="small" value={tplAmountDisplay}
                                    onChange={e => handleAmountInput(e.target.value, setTplForm, "amount", setTplAmountDisplay)}
                                    InputProps={{ endAdornment: <InputAdornment position="end">원</InputAdornment> }}
                                    inputProps={{ inputMode: "numeric" }} placeholder="0" />
                            </FormField>
                            <FormField label="매월 지급일" required>
                                <TextField size="small" type="number" value={tplForm.payDay}
                                    onChange={e => setTplForm(f => ({ ...f, payDay: e.target.value }))}
                                    inputProps={{ min: 1, max: 31 }} />
                            </FormField>
                        </Box>
                        {/* 유효기간 — '고정비 달력' 탭과 동일한 필드. 비우면 무기한. */}
                        <Box sx={{ display: "flex", gap: 1.5 }}>
                            <FormField label="시작일 (비우면 무기한)">
                                <TextField size="small" type="date" fullWidth value={tplForm.startDate}
                                    onChange={e => setTplForm(f => ({ ...f, startDate: e.target.value }))}
                                    InputLabelProps={{ shrink: true }} />
                            </FormField>
                            <FormField label="종료일 (비우면 무기한)">
                                <TextField size="small" type="date" fullWidth value={tplForm.endDate}
                                    onChange={e => setTplForm(f => ({ ...f, endDate: e.target.value }))}
                                    InputLabelProps={{ shrink: true }} />
                            </FormField>
                        </Box>
                        <FormField label="거래처">
                            <VendorAutocomplete freeSolo value={tplForm.vendorId} initialName={tplForm.vendorName}
                                onChange={(id, vendorOrText) => {
                                    if (typeof vendorOrText === "string") {
                                        setTplForm(f => ({ ...f, vendorId: null, vendorName: vendorOrText }));
                                    } else if (vendorOrText) {
                                        setTplForm(f => ({ ...f, vendorId: id, vendorName: vendorOrText.name }));
                                    } else {
                                        setTplForm(f => ({ ...f, vendorId: null, vendorName: "" }));
                                    }
                                }}
                                label={null} />
                        </FormField>
                        <FormField label="비고">
                            <TextField size="small" value={tplForm.note}
                                onChange={e => setTplForm(f => ({ ...f, note: e.target.value }))}
                                placeholder="예: 매월 26일 결제 / 71 계좌" />
                        </FormField>
                        <FormControlLabel
                            control={<Switch size="small" checked={!!tplForm.active}
                                onChange={e => setTplForm(f => ({ ...f, active: e.target.checked }))} />}
                            label={<Typography sx={{ fontSize: 12 }}>활성 (월 생성에 포함)</Typography>} />
                        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                            <ActionButton variant="secondary" onClick={() => { setTplForm(null); setTplEditing(null); }}
                                label="목록으로" sx={{ fontSize: 12 }} />
                            <ActionButton variant="add" onClick={saveTemplate}
                                label={tplEditing ? "수정 저장" : "등록"}
                                disabled={!tplForm.title || !tplForm.amount || !tplForm.payDay}
                                sx={{ fontSize: 12 }} />
                        </Box>
                    </Stack>
                )}
            </FormModal>
        </Box>
    );
}
